import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Sun, Zap, FolderCheck, Users } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function useCountUp(target, durationMs = 1400, decimals = 0) {
  const [value, setValue] = useState(0);
  const startedRef = useRef(false);
  useEffect(() => {
    if (startedRef.current) return;
    if (target == null) return;
    startedRef.current = true;
    const start = performance.now();
    const tick = (t) => {
      const p = Math.min(1, (t - start) / durationMs);
      const eased = 1 - Math.pow(1 - p, 3);
      setValue(target * eased);
      if (p < 1) requestAnimationFrame(tick);
      else setValue(target);
    };
    requestAnimationFrame(tick);
  }, [target, durationMs]);
  return decimals > 0 ? value.toFixed(decimals) : Math.round(value).toLocaleString();
}

function StatTile({ icon: Icon, label, value, suffix = "", decimals = 0, testId }) {
  const display = useCountUp(value, 1400, decimals);
  return (
    <div
      data-testid={testId}
      className="rounded-xl border border-white/10 bg-white/5 backdrop-blur-sm p-6 hover:bg-white/10 transition group"
    >
      <div className="flex items-center gap-3 mb-3">
        <div className="w-10 h-10 rounded-md bg-[#C9A356]/20 grid place-items-center group-hover:bg-[#C9A356]/30 transition">
          <Icon className="w-5 h-5 text-[#C9A356]" />
        </div>
        <p className="text-[10px] uppercase tracking-[0.2em] text-white/60">{label}</p>
      </div>
      <div className="font-heading text-4xl lg:text-5xl font-medium text-white tabular-nums">
        {value == null ? "…" : display}
        <span className="text-[#C9A356] ml-1 text-2xl lg:text-3xl">{suffix}</span>
      </div>
    </div>
  );
}

export default function LiveCounter() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const r = await axios.get(`${API}/stats/public`);
        if (!cancelled) setStats(r.data);
      } catch (err) {
        // Public counter is best-effort; log but don't render an error to landing visitors.
        console.warn("Public stats fetch failed:", err);
      }
    };
    load();
    const interval = setInterval(load, 60_000); // refresh every minute
    return () => { cancelled = true; clearInterval(interval); };
  }, []);

  return (
    <section
      data-testid="live-counter"
      className="relative z-10 max-w-6xl mx-auto px-6 py-14 border-t border-white/10"
    >
      <div className="flex items-end justify-between mb-8">
        <div>
          <p className="overline text-[#C9A356]">Live Impact</p>
          <h2 className="font-heading text-3xl lg:text-4xl text-white mt-2">
            Powering St. Maarten, one project at a time.
          </h2>
        </div>
        <div className="hidden md:flex items-center gap-2 text-xs text-white/50">
          <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse"></span>
          Updates in real-time
        </div>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatTile testId="stat-panels" icon={Sun} label="Panels installed" value={stats?.panels_installed} />
        <StatTile testId="stat-mw" icon={Zap} label="MW deployed" value={stats?.mw_deployed} suffix="MW" decimals={2} />
        <StatTile testId="stat-projects" icon={FolderCheck} label="Projects completed" value={stats?.projects_completed} />
        <StatTile testId="stat-clients" icon={Users} label="Clients served" value={stats?.clients_served} />
      </div>
    </section>
  );
}
